import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

import useOutsideClick from '../../../hooks/useOutsideClicks';
import { Tooltip, InfoModal } from '../../../components';
import {
  emailValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
} from '../../auth/components/ValidationSchemaItems';
import { LeadsListProps } from '../../../core/types/_models';
import { createLead, updateLead } from '../../../core/requests/requests';

interface AddLeadModalProps {
  onClose: () => void;
  updateLeadsList: () => void;
  leadId?: string | null;
  leadData?: LeadsListProps | null;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({
  onClose,
  updateLeadsList,
  leadId,
  leadData,
}) => {
  const [infoModalMessage, setInfoModalMessage] = useState<string>('');
  const showEditModalRef = useRef<HTMLDivElement>(null);

  useOutsideClick(showEditModalRef, () => onClose());

  const handleCloseInfoModal = () => {
    setInfoModalMessage('');
    onClose();
  };

  const initialFormikValues: LeadsListProps = {
    id: leadData?.id ?? '',
    createdAt: leadData?.createdAt ?? '',
    updatedAt: leadData?.updatedAt ?? '',
    origin: leadData?.origin ?? '',
    Tags: leadData?.Tags ?? [],
    customerId: leadData?.customerId ?? '',
    firstname: leadData?.firstname ?? '',
    lastname: leadData?.lastname ?? '',
    email: leadData?.email ?? '',
    phone: leadData?.phone ?? '',
    city: leadData?.city ?? '',
    company: leadData?.company ?? '',
    jobtitle: leadData?.jobtitle ?? '',
    state: leadData?.state ?? '',
    facebook: leadData?.facebook ?? '',
    instagram: leadData?.instagram ?? '',
    note: leadData?.note ?? '',
  };

  const validationSchema = Yup.object().shape({
    firstname: firstNameValidationSchema.required('First name is required'),
    lastname: lastNameValidationSchema.required('Last name is required'),
    email: emailValidationSchema.required('Email is required'),
    phone: phoneNumberValidationSchema.required('Phone is required'),
  });

  const handleSubmit = async (values: LeadsListProps) => {
    try {
      const request = leadId ? updateLead(leadId, values) : createLead(values);

      const { data } = await request;
      if (data) {
        updateLeadsList();
        onClose();
      }
    } catch (error: any) {
      setInfoModalMessage(
        error?.response?.data?.message || 'An error occurred'
      );
    }
  };

  return (
    <>
      <div className="add-lead-modal-overlay">
        <div className="add-lead-modal-container" ref={showEditModalRef}>
          <Formik
            initialValues={initialFormikValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ errors, isValid, touched }) => (
              <Form className="add-lead-modal-form">
                {/* Header */}
                <div className="add-lead-modal-header">
                  <div className="add-lead-modal-header-title">
                    <span className="add-lead-modal-title-text">
                      {leadId ? 'Edit Lead' : 'Add New Lead'}
                    </span>
                    <Tooltip
                      tooltipText="Manage lead contact details and social links."
                      tooltipWidth={200}
                    />
                  </div>
                  <div className="add-lead-modal-actions">
                    <button
                      type="button"
                      className="add-lead-modal-btn-cancel"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="add-lead-modal-btn-save"
                      disabled={!isValid}
                    >
                      Save
                    </button>
                  </div>
                </div>

                <div className="add-lead-modal-grid">
                  {/* Column 1: Primary Info */}
                  <div className="add-lead-modal-column">
                    <div className="add-lead-modal-field-group">
                      <label htmlFor="firstname">First Name *</label>
                      <Field
                        name="firstname"
                        placeholder="First Name"
                        className="add-lead-modal-input"
                      />
                      {errors.firstname && touched.firstname && (
                        <div className="add-lead-modal-error">
                          {errors.firstname}
                        </div>
                      )}
                    </div>

                    <div className="add-lead-modal-field-group">
                      <label htmlFor="lastname">Last Name *</label>
                      <Field
                        name="lastname"
                        placeholder="Last Name"
                        className="add-lead-modal-input"
                      />
                      {errors.lastname && touched.lastname && (
                        <div className="add-lead-modal-error">
                          {errors.lastname}
                        </div>
                      )}
                    </div>

                    <div className="add-lead-modal-field-group">
                      <label htmlFor="email">Email *</label>
                      <Field
                        name="email"
                        placeholder="example@mail.com"
                        className="add-lead-modal-input"
                      />
                      {errors.email && touched.email && (
                        <div className="add-lead-modal-error">
                          {errors.email}
                        </div>
                      )}
                    </div>

                    <div className="add-lead-modal-field-group">
                      <label htmlFor="phone">Phone *</label>
                      <Field
                        name="phone"
                        placeholder="(123) 456-7890"
                        className="add-lead-modal-input"
                      />
                      {errors.phone && touched.phone && (
                        <div className="add-lead-modal-error">
                          {errors.phone}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Column 2: Professional Info */}
                  <div className="add-lead-modal-column">
                    <div className="add-lead-modal-field-group">
                      <label htmlFor="company">Company</label>
                      <Field
                        name="company"
                        placeholder="Company Name"
                        className="add-lead-modal-input"
                      />
                    </div>

                    <div className="add-lead-modal-field-group">
                      <label htmlFor="jobtitle">Job Title</label>
                      <Field
                        name="jobtitle"
                        placeholder="Software Engineer"
                        className="add-lead-modal-input"
                      />
                    </div>

                    <div className="add-lead-modal-field-group">
                      <label htmlFor="city">City</label>
                      <Field
                        name="city"
                        placeholder="City"
                        className="add-lead-modal-input"
                      />
                    </div>

                    <div className="add-lead-modal-field-group">
                      <label htmlFor="state">State</label>
                      <Field
                        name="state"
                        placeholder="State"
                        className="add-lead-modal-input"
                      />
                    </div>
                  </div>

                  {/* Column 3: Social Info */}
                  <div className="add-lead-modal-column">
                    <div className="add-lead-modal-field-group">
                      <label htmlFor="facebook">Facebook URL</label>
                      <Field
                        name="facebook"
                        placeholder="facebook.com/username"
                        className="add-lead-modal-input"
                      />
                    </div>

                    <div className="add-lead-modal-field-group">
                      <label htmlFor="instagram">Instagram URL</label>
                      <Field
                        name="instagram"
                        placeholder="@username"
                        className="add-lead-modal-input"
                      />
                    </div>
                    <div className="add-lead-modal-field-group">
                      <label htmlFor="note">Notes</label>
                      <Field
                        as="textarea"
                        rows={4}
                        name="note"
                        placeholder="Note"
                        className="add-lead-modal-textarea"
                      />
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {infoModalMessage && (
        <div className="add-lead-modal-error-overlay">
          <InfoModal title={infoModalMessage} onClose={handleCloseInfoModal} />
        </div>
      )}
    </>
  );
};

export default AddLeadModal;
